import { FileText, MoreHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { displayDate } from '@/lib/dates'
import { postJSON } from '@/lib/api'
import type { ContactGiving } from '@/lib/contacts/types'

export function ContactGivingTab({ giving }: { giving: ContactGiving | undefined }) {
  const resendReceipt = (emailSendId: number) =>
    postJSON(`/api/v1/email_sends/${emailSendId}/resend`, {})
  if (!giving || giving.gifts.length === 0) return <EmptyState title="No gifts recorded." />

  return (
    <div className="space-y-4">
      <section className="flex flex-wrap gap-x-8 gap-y-3 rounded-md border border-border bg-card px-4 py-3">
        <Summary label="Total given" value={giving.summary.total_given} />
        <Summary label="This year" value={giving.summary.this_year} />
        <Summary label="First gift" value={giving.summary.first_gift ?? '—'} />
      </section>

      {giving.recurring.length > 0 && (
        <section className="rounded-md border border-border bg-card p-4">
          <h3 className="mb-3 text-body font-semibold">Recurring gift</h3>
          <div className="space-y-3">
            {giving.recurring.map((gift) => (
              <div key={gift.id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1 text-body-sm">
                  <p>
                    {gift.amount} · {capitalize(gift.frequency)} · next charge{' '}
                    {displayDate(gift.next_charge_on)}
                  </p>
                  <p className="text-caption text-muted-foreground">
                    {gift.card ?? 'Payment method unavailable'} · {gift.campaign}
                  </p>
                </div>
                <Badge variant="neutral">Active</Badge>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-md border border-border bg-card p-4">
        <h3 className="mb-3 text-body font-semibold">Gifts</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {giving.gifts.map((gift) => (
              <TableRow
                key={gift.id}
                className={gift.refunded ? 'text-muted-foreground' : undefined}
              >
                <TableCell className="text-caption">{displayDate(gift.gift_on)}</TableCell>
                <TableCell className="text-body-sm">{gift.campaign ?? 'General'}</TableCell>
                <TableCell className="text-caption">{gift.method}</TableCell>
                <TableCell>
                  {gift.receipt_email_send_id ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="size-8 px-0"
                          aria-label="Gift actions"
                        >
                          <MoreHorizontal className="size-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          onSelect={() => resendReceipt(gift.receipt_email_send_id!)}
                        >
                          <FileText className="mr-2 size-3.5" /> Resend receipt
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  {gift.amount}
                  {gift.refunded && (
                    <Badge variant="neutral" className="ml-2">
                      Refunded
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  )
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-caption text-muted-foreground">{label}</span>
      <span className="text-body-sm">{value}</span>
    </div>
  )
}

function capitalize(value: string) {
  return value.slice(0, 1).toUpperCase() + value.slice(1)
}
